import React, { useCallback, useState, useMemo, useEffect } from 'react';
import {
  CommandSelect,
  CommandSelectTrigger,
  CommandSelectValue,
  CommandSelectContent,
  CommandSelectItem,
  CommandSelectAll,
  CommandSelectEmpty,
  useCommandSelectSearch,
} from './command-select';
import { Group } from './types';

export interface GroupsSelectProps {
  children: React.ReactNode;
}

/**
 * GroupsSelect renders a multi-select dropdown of groups.
 */
export const GroupsSelect: React.FC<GroupsSelectProps> = ({ children }) => {
  return <div className="relative">{children}</div>;
};

export const GroupsOptions = ({
  groups,
  isLoading,
  value,
  onChange,
}: {
  groups: Group[];
  isLoading: boolean;
  value: number[];
  onChange: (ids: number[]) => void;
}) => {
  const [selectedGroupOptions, setSelectedGroupOptions] = useState<number[]>(value || []);
  const [isUpdating, setIsUpdating] = useState(false);

  // Sync with form values when not updating
  useEffect(() => {
    if (!isUpdating) {
      const newValue = value || [];

      // Only update if arrays are actually different (set equality check - order independent)
      const arraysAreEqual = selectedGroupOptions.length === newValue.length &&
        selectedGroupOptions.every((id) => newValue.includes(id));

      if (!arraysAreEqual) {
        setSelectedGroupOptions(newValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isUpdating]); // selectedGroupOptions intentionally excluded to prevent infinite loop

  // Reset isUpdating flag when selectedGroupOptions changes
  useEffect(() => {
    if (isUpdating) {
      setIsUpdating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupOptions]); // Only listen to selectedGroupOptions changes

  // Unified update handler with proper state management
  const updateSelection = useCallback(
    (newSelection: number[]) => {
      setIsUpdating(true);
      setSelectedGroupOptions(newSelection);
      onChange(newSelection);
    },
    [onChange],
  );

  const handleDismiss = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      updateSelection([]);
    },
    [updateSelection],
  );

  const getDisplayText = () => {
    if (selectedGroupOptions.length === 0) {
      return { mainText: 'Select groups...', count: null };
    }
    if (selectedGroupOptions.length === 1) {
      const group = groups.find((g) => g.id === selectedGroupOptions[0]);
      return { mainText: group ? group.name : 'Select groups...', count: null };
    }
    const firstGroup = groups.find((g) => g.id === selectedGroupOptions[0]);
    return {
      mainText: firstGroup?.name || 'Select groups...',
      count: selectedGroupOptions.length - 1
    };
  };

  return (
    <CommandSelect>
      <CommandSelectTrigger placeholder="Select groups...">
        <CommandSelectValue
          placeholder="Select groups..."
          displayText={selectedGroupOptions.length > 0 ? getDisplayText().mainText : undefined}
          count={getDisplayText().count}
          onClear={handleDismiss}
          disabled={isUpdating}
        />
      </CommandSelectTrigger>
      <CommandSelectContent searchPlaceholder="Search groups..." maxHeight="200px">
        <GroupsContentWithSearch
          groups={groups}
          isLoading={isLoading}
          selectedGroupOptions={selectedGroupOptions}
          isUpdating={isUpdating}
          updateSelection={updateSelection}
        />
      </CommandSelectContent>
    </CommandSelect>
  );
};

// Component that uses the search context for filtering
const GroupsContentWithSearch = ({
  groups,
  isLoading,
  selectedGroupOptions,
  isUpdating,
  updateSelection,
}: {
  groups: Group[];
  isLoading: boolean;
  selectedGroupOptions: number[];
  isUpdating: boolean;
  updateSelection: (newSelection: number[]) => void;
}) => {
  // Get the search query from the CommandSelect context
  const searchQuery = useCommandSelectSearch();

  // Get filtered groups based on search query from context
  const filteredGroups = useMemo(() => {
    if (!searchQuery) return groups;
    const query = searchQuery.toLowerCase();
    return groups.filter((group) => group.name.toLowerCase().includes(query));
  }, [groups, searchQuery]);

  // Calculate select all state for currently visible groups
  const getSelectAllState = useMemo(() => {
    const visibleGroupIds = filteredGroups.map((g) => g.id);
    const selectedVisibleIds = selectedGroupOptions.filter((id) => visibleGroupIds.includes(id));

    return {
      checked: visibleGroupIds.length > 0 && selectedVisibleIds.length === visibleGroupIds.length,
      indeterminate: selectedVisibleIds.length > 0 && selectedVisibleIds.length < visibleGroupIds.length,
      disabled: visibleGroupIds.length === 0 || isLoading || isUpdating
    };
  }, [filteredGroups, selectedGroupOptions, isLoading, isUpdating]);

  // Handle select all/deselect all for currently visible groups
  const handleSelectAllToggle = useCallback(() => {
    if (isUpdating) {
      return;
    }

    const visibleGroupIds = filteredGroups.map((g) => g.id);
    const selectedVisibleIds = selectedGroupOptions.filter((id) => visibleGroupIds.includes(id));

    let newSelection: number[];

    if (selectedVisibleIds.length === visibleGroupIds.length) {
      // Deselect all visible groups (preserve selections from other filter contexts)
      newSelection = selectedGroupOptions.filter((id) => !visibleGroupIds.includes(id));
    } else {
      // Select all visible groups (preserve existing selections)
      newSelection = [...new Set([...selectedGroupOptions, ...visibleGroupIds])];
    }

    updateSelection(newSelection);
  }, [filteredGroups, selectedGroupOptions, updateSelection, isUpdating]);

  const handleSelectGroup = useCallback(
    (groupId: number) => {
      if (isUpdating) return;

      const newValue = selectedGroupOptions.includes(groupId)
        ? selectedGroupOptions.filter((v) => v !== groupId)
        : [...selectedGroupOptions, groupId];

      updateSelection(newValue);
    },
    [selectedGroupOptions, updateSelection, isUpdating],
  );

  return (
    <>
      {/* Select All/Deselect All - always shown as first item */}
      {groups.length > 0 && (
        <CommandSelectAll
          checked={getSelectAllState.checked}
          indeterminate={getSelectAllState.indeterminate}
          disabled={getSelectAllState.disabled}
          onToggle={handleSelectAllToggle}
          count={filteredGroups.length}
        />
      )}

      {filteredGroups.length > 0 ? (
        filteredGroups.map((group) => {
          const isSelected = selectedGroupOptions.includes(group.id);

          return (
            <CommandSelectItem
              key={group.id}
              value={String(group.id)}
              onSelect={() => handleSelectGroup(group.id)}
              selected={isSelected}
              disabled={isUpdating}
            >
              <span>{group.name}</span>
            </CommandSelectItem>
          );
        })
      ) : (
        <CommandSelectEmpty>No groups found.</CommandSelectEmpty>
      )}
    </>
  );
};
